import Typography from '@mui/material/Typography';
import { differenceInDays, endOfDay, format, isSameYear } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './overdue-context-menu.style';

export enum EntryTypes {
  Complete = 'complete',
  Skip = 'skip',
  Failure = 'failure',
}

export type OverdueContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'onOutside' | 'children'
> & {
  name: string;
  overdueDate: Timestamp;
  type?: EntryTypes;
  onDate: (date: Timestamp) => void;
  onClose: () => void;
};

export const OverdueContextMenu: React.FC<OverdueContextMenuProps> = ({
  name,
  overdueDate,
  onDate,
  onClose,
  type = EntryTypes.Complete,
  ...rest
}) => {
  const today = useToday();
  const { t } = useTranslation();

  const onOverdue = () => {
    onDate(overdueDate);
    onClose();
  };
  const onToday = () => {
    onDate(today);
    onClose();
  };

  const title = t(
    type === EntryTypes.Failure
      ? 'general.labels.task-detail.checkbox.overdue-options.fail.title'
      : type === EntryTypes.Skip
        ? 'general.labels.task-detail.checkbox.overdue-options.skip.title'
        : 'general.labels.task-detail.checkbox.overdue-options.complete.title',
    { name },
  );

  const overdueLabel = useMemo(
    () =>
      isSameYear(overdueDate, today)
        ? format(overdueDate, 'MMM d')
        : format(overdueDate, 'MMM d, yy'),
    [overdueDate, today],
  );
  const daysOverdue = useMemo(
    () =>
      t('general.labels.days-ago', {
        count: differenceInDays(endOfDay(today), endOfDay(overdueDate)),
      }),
    [overdueDate, t, today],
  );

  return (
    <PopupMenu {...rest} onOutside={onClose}>
      <Styled.ListWidthLimiter>
        <PopupMenuList>
          <PopupMenuTitle title={title} />
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={Calendar16} />}
              onClick={onOverdue}
              end={<Typography variant="overline">{daysOverdue}</Typography>}
            >
              {overdueLabel}
            </PopupMenuButton>
            <PopupMenuButton
              start={<Icon icon={Calendar16} />}
              onClick={onToday}
            >
              {t('general.labels.dates.today')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      </Styled.ListWidthLimiter>
    </PopupMenu>
  );
};
